import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';

const PLAN_COLORS = {
  starter: 'bg-gray-100 text-gray-700',
  professional: 'bg-blue-100 text-blue-700',
  enterprise: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
  active: 'bg-success/10 text-success',
  trialing: 'bg-yellow-100 text-yellow-700',
  past_due: 'bg-destructive/10 text-destructive',
  cancelled: 'bg-gray-100 text-gray-500',
  paused: 'bg-orange-100 text-orange-700',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const StatCard = ({ icon, label, value, sub = '', color, trend, trendUp }) => (
  <div className="bg-card rounded-xl border border-border p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon name={icon} size={18} color="currentColor" />
      </div>
    </div>
    <div className="text-3xl font-bold text-foreground">{value}</div>
    <div className="flex items-center gap-2 mt-1">
      {trend && (
        <span className={`flex items-center gap-0.5 text-xs font-medium ${trendUp ? 'text-green-600' : 'text-red-500'}`}>
          <Icon name={trendUp ? 'TrendingUp' : 'TrendingDown'} size={12} color="currentColor" />
          {trend}
        </span>
      )}
      {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </div>
  </div>
);

const generateRevenueData = () => {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  let mrr = 4200;
  return months?.map(month => {
    mrr += Math.floor(Math.random() * 800 + 200);
    return { month, mrr, newOrgs: Math.floor(Math.random() * 4 + 1), churn: Math.floor(Math.random() * 2) };
  });
};

const generateUserGrowthData = () => {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  let users = 45;
  return months?.map(month => {
    users += Math.floor(Math.random() * 12 + 3);
    return { month, users, active: Math.floor(users * (0.8 + Math.random() * 0.15)) };
  });
};

const AdminOperatorDashboard = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [revenueData] = useState(generateRevenueData);
  const [userGrowthData] = useState(generateUserGrowthData);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [orgsRes, subsRes] = await Promise.all([
        supabase?.from('organizations')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('org_subscriptions')?.select('*'),
      ]);
      if (orgsRes?.error) throw orgsRes?.error;
      if (subsRes?.error) throw subsRes?.error;
      setOrganizations(orgsRes?.data || []);
      setSubscriptions(subsRes?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load organizations.');
    } finally {
      setLoading(false);
    }
  };

  const getOrgSubscription = (orgId) => subscriptions?.find(s => s?.organization_id === orgId);

  const toggleOrgStatus = async (org) => {
    setActionLoading(org?.id);
    try {
      const { error } = await supabase?.from('organizations')?.update({ is_active: !org?.is_active })?.eq('id', org?.id);
      if (error) throw error;
      setOrganizations(prev => prev?.map(o => o?.id === org?.id ? { ...o, is_active: !o?.is_active } : o));
      if (selectedOrg?.id === org?.id) setSelectedOrg(prev => ({ ...prev, is_active: !prev?.is_active }));
    } catch (err) {
      setError(err?.message || 'Failed to update organization.');
    } finally {
      setActionLoading('');
    }
  };

  const updateSubscriptionStatus = async (orgId, newStatus) => {
    setActionLoading(orgId + '-sub');
    try {
      const { error } = await supabase?.from('org_subscriptions')?.update({ status: newStatus })?.eq('organization_id', orgId);
      if (error) throw error;
      setSubscriptions(prev => prev?.map(s => s?.organization_id === orgId ? { ...s, status: newStatus } : s));
    } catch (err) {
      setError(err?.message || 'Failed to update subscription.');
    } finally {
      setActionLoading('');
    }
  };

  const filteredOrgs = organizations?.filter(org => {
    const sub = getOrgSubscription(org?.id);
    const matchSearch = !search || org?.name?.toLowerCase()?.includes(search?.toLowerCase()) || org?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = filterStatus === 'all' || (filterStatus === 'active' ? org?.is_active : !org?.is_active);
    const matchPlan = filterPlan === 'all' || sub?.plan === filterPlan;
    return matchSearch && matchStatus && matchPlan;
  });

  // Stats
  const totalOrgs = organizations?.length;
  const activeOrgs = organizations?.filter(o => o?.is_active)?.length;
  const totalUsers = subscriptions?.reduce((sum, s) => sum + (s?.current_user_count || 0), 0);
  const totalMRR = subscriptions?.reduce((sum, s) => sum + ((s?.price_per_user_monthly || 0) * (s?.current_user_count || 0)), 0);
  const trialingOrgs = subscriptions?.filter(s => s?.status === 'trialing')?.length;
  const pastDueOrgs = subscriptions?.filter(s => s?.status === 'past_due')?.length;

  const planDistribution = [
    { name: 'Starter', value: subscriptions?.filter(s => s?.plan === 'starter')?.length || 0 },
    { name: 'Professional', value: subscriptions?.filter(s => s?.plan === 'professional')?.length || 0 },
    { name: 'Enterprise', value: subscriptions?.filter(s => s?.plan === 'enterprise')?.length || 0 },
  ]?.filter(p => p?.value > 0);

  const orgPerformance = organizations?.slice(0, 5)?.map(org => {
    const sub = getOrgSubscription(org?.id);
    return {
      name: org?.name?.length > 14 ? org?.name?.substring(0, 14) + '...' : org?.name,
      users: sub?.current_user_count || 0,
      mrr: (sub?.price_per_user_monthly || 0) * (sub?.current_user_count || 0),
    };
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'organizations', label: 'Organizations', icon: 'Building2' },
  ];

  return (
    <>
      <Helmet><title>Admin Operator Dashboard - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={16} color="#7c3aed" />
                </div>
                <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Super Admin</span>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Admin Operator Dashboard</h1>
              <p className="text-muted-foreground text-sm mt-1">Manage all organizations, subscriptions, and platform analytics.</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/audit-activity-log')}
                className="flex items-center gap-2 border border-border px-4 py-2.5 rounded-lg font-medium hover:bg-muted transition-smooth text-sm"
              >
                <Icon name="Activity" size={16} color="currentColor" />
                Audit Log
              </button>
              <button
                onClick={() => navigate('/organization-onboarding')}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-smooth"
              >
                <Icon name="Plus" size={16} color="white" />
                Onboard New Org
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <StatCard icon="Building2" label="Total Orgs" value={totalOrgs} color="bg-blue-100 text-blue-600" trend="+2 this month" trendUp={true} />
            <StatCard icon="CheckCircle" label="Active Orgs" value={activeOrgs} sub={`${totalOrgs - activeOrgs} inactive`} color="bg-success/10 text-success" trend="" trendUp={false} />
            <StatCard icon="Users" label="Total Users" value={totalUsers} color="bg-orange-100 text-orange-600" trend="+8 this month" trendUp={true} />
            <StatCard icon="DollarSign" label="MRR" value={`$${totalMRR?.toLocaleString()}`} sub="est. monthly" color="bg-green-100 text-green-600" trend="+12.4%" trendUp={true} />
            <StatCard icon="Clock" label="Trialing" value={trialingOrgs} sub="in free trial" color="bg-yellow-100 text-yellow-600" trend="" trendUp={false} />
            <StatCard icon="AlertTriangle" label="Past Due" value={pastDueOrgs} sub="need attention" color="bg-red-100 text-red-600" trend="" trendUp={false} />
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 border-b border-border">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-smooth -mb-px ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={15} color="currentColor" />
                {tab?.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
              <button onClick={() => setError('')} className="ml-auto"><Icon name="X" size={14} color="var(--color-destructive)" /></button>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* MRR Trend */}
                <div className="lg:col-span-2 bg-card rounded-xl border border-border p-5">
                  <h3 className="font-heading font-semibold text-foreground mb-4">MRR Growth Trend</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={revenueData}>
                      <defs>
                        <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${(v / 1000)?.toFixed(1)}k`} />
                      <Tooltip formatter={v => [`$${v?.toLocaleString()}`, 'MRR']} />
                      <Area type="monotone" dataKey="mrr" name="MRR" stroke="#7c3aed" fill="url(#mrrGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Plan Distribution */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <h3 className="font-heading font-semibold text-foreground mb-4">Plan Distribution</h3>
                  {planDistribution?.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                          <Pie data={planDistribution} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                            {planDistribution?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS?.[index % PIE_COLORS?.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-2 mt-2">
                        {planDistribution?.map((item, i) => (
                          <div key={item?.name} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS?.[i] }} />
                              <span className="text-muted-foreground">{item?.name}</span>
                            </div>
                            <span className="font-semibold">{item?.value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                      <Icon name="PieChart" size={32} color="currentColor" className="mb-2 opacity-40" />
                      <p className="text-sm">No subscription data</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Org Performance Table */}
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-foreground">Top Organizations by Revenue</h3>
                  <button onClick={() => setActiveTab('organizations')} className="text-xs text-primary hover:underline">View All</button>
                </div>
                {orgPerformance?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={orgPerformance} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip formatter={v => [`$${v}`, 'MRR']} />
                      <Bar dataKey="mrr" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <Icon name="BarChart3" size={32} color="currentColor" className="mb-2 opacity-40" />
                    <p className="text-sm">No organization data yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User Growth */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <h3 className="font-heading font-semibold text-foreground mb-4">User Growth Trend</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={userGrowthData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="users" name="Total Users" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="active" name="Active Users" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* New Orgs & Churn */}
                <div className="bg-card rounded-xl border border-border p-5">
                  <h3 className="font-heading font-semibold text-foreground mb-4">New Organizations vs Churn</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="newOrgs" name="New Orgs" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="churn" name="Churned" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Revenue per Org */}
              <div className="bg-card rounded-xl border border-border p-5">
                <h3 className="font-heading font-semibold text-foreground mb-4">Organization Performance Metrics</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">Organization</th>
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">Plan</th>
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">Users</th>
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">MRR</th>
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-muted-foreground font-medium">Sub Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {organizations?.slice(0, 10)?.map(org => {
                        const sub = getOrgSubscription(org?.id);
                        let mrr = (sub?.price_per_user_monthly || 0) * (sub?.current_user_count || 0);
                        return (
                          <tr key={org?.id} className="hover:bg-muted/40 transition-smooth">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                                  <span className="text-primary font-bold text-xs">{org?.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <span className="font-medium text-foreground truncate max-w-32">{org?.name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {sub ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLAN_COLORS?.[sub?.plan] || 'bg-gray-100 text-gray-600'}`}>{sub?.plan}</span> : <span className="text-muted-foreground">-</span>}
                            </td>
                            <td className="py-3 px-4 text-foreground">{sub?.current_user_count || 0}</td>
                            <td className="py-3 px-4 font-semibold text-foreground">${mrr}/mo</td>
                            <td className="py-3 px-4">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${org?.is_active ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'}`}>
                                {org?.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {sub ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS?.[sub?.status] || ''}`}>{sub?.status}</span> : <span className="text-muted-foreground">-</span>}
                            </td>
                          </tr>
                        );
                      })}
                      {organizations?.length === 0 && (
                        <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No organizations found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <div className="bg-card rounded-xl border border-border">
                  <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search organizations..."
                        value={search}
                        onChange={e => setSearch(e?.target?.value)}
                        className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none">
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <select value={filterPlan} onChange={e => setFilterPlan(e?.target?.value)} className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none">
                      <option value="all">All Plans</option>
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  {loading ? (
                    <div className="p-12 text-center">
                      <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin mx-auto mb-3" />
                      <p className="text-muted-foreground">Loading organizations...</p>
                    </div>
                  ) : filteredOrgs?.length === 0 ? (
                    <div className="p-12 text-center">
                      <Icon name="Building2" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                      <p className="text-muted-foreground">No organizations found.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredOrgs?.map(org => {
                        const sub = getOrgSubscription(org?.id);
                        let mrr = (sub?.price_per_user_monthly || 0) * (sub?.current_user_count || 0);
                        return (
                          <div
                            key={org?.id}
                            onClick={() => setSelectedOrg(org)}
                            className={`p-4 cursor-pointer hover:bg-muted/50 transition-smooth ${
                              selectedOrg?.id === org?.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary font-bold text-sm">{org?.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <div className="min-w-0">
                                  <p className="font-semibold text-foreground truncate">{org?.name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{org?.email}</p>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  org?.is_active ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'
                                }`}>{org?.is_active ? 'Active' : 'Inactive'}</span>
                                {sub && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLAN_COLORS?.[sub?.plan] || 'bg-gray-100 text-gray-600'}`}>{sub?.plan}</span>}
                              </div>
                            </div>
                            <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><Icon name="Users" size={12} color="currentColor" />{sub?.current_user_count || 0} users</span>
                              <span className="flex items-center gap-1"><Icon name="DollarSign" size={12} color="currentColor" />${mrr}/mo</span>
                              {sub && <span className={`px-2 py-0.5 rounded-full ${STATUS_COLORS?.[sub?.status] || 'bg-gray-100 text-gray-500'}`}>{sub?.status}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {selectedOrg && (() => {
                const sub = getOrgSubscription(selectedOrg?.id);
                let mrr = (sub?.price_per_user_monthly || 0) * (sub?.current_user_count || 0);
                return (
                  <div className="lg:w-96">
                    <div className="bg-card rounded-xl border border-border sticky top-24">
                      <div className="p-5 border-b border-border flex items-center justify-between">
                        <h3 className="font-heading font-semibold text-foreground">Organization Details</h3>
                        <button onClick={() => setSelectedOrg(null)} className="p-1 hover:bg-muted rounded">
                          <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                        </button>
                      </div>
                      <div className="p-5 space-y-4">
                        <div>
                          <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                            <span className="text-primary font-bold text-xl">{selectedOrg?.name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <h4 className="font-bold text-foreground text-lg">{selectedOrg?.name}</h4>
                          <p className="text-sm text-muted-foreground">{selectedOrg?.email}</p>
                          {selectedOrg?.phone && <p className="text-sm text-muted-foreground">{selectedOrg?.phone}</p>}
                          {selectedOrg?.city && <p className="text-sm text-muted-foreground">{selectedOrg?.city}, {selectedOrg?.state}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                            selectedOrg?.is_active ? 'bg-success/10 text-success' : 'bg-gray-100 text-gray-500'
                          }`}>{selectedOrg?.is_active ? 'Active' : 'Inactive'}</span>
                          {sub && <span className={`text-xs font-medium px-3 py-1 rounded-full ${STATUS_COLORS?.[sub?.status] || ''}`}>{sub?.status}</span>}
                        </div>
                        {sub && (
                          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Subscription</p>
                            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Plan</span><span className={`font-medium px-2 py-0.5 rounded-full text-xs ${PLAN_COLORS?.[sub?.plan]}`}>{sub?.plan}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Users</span><span className="font-medium">{sub?.current_user_count} / {sub?.max_users === 999 ? '∞' : sub?.max_users}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Price/User</span><span className="font-medium">${sub?.price_per_user_monthly}/mo</span></div>
                            <div className="flex justify-between text-sm"><span className="text-muted-foreground">MRR</span><span className="font-bold text-primary">${mrr}/mo</span></div>
                            {sub?.trial_ends_at && sub?.status === 'trialing' && (
                              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Trial Ends</span><span className="font-medium">{new Date(sub.trial_ends_at)?.toLocaleDateString()}</span></div>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                          <p>Created: {new Date(selectedOrg.created_at)?.toLocaleDateString()}</p>
                        </div>
                        <div className="space-y-2 pt-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</p>
                          <button
                            onClick={() => toggleOrgStatus(selectedOrg)}
                            disabled={actionLoading === selectedOrg?.id}
                            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-smooth ${
                              selectedOrg?.is_active
                                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20' : 'bg-success/10 text-success hover:bg-success/20'
                            }`}
                          >
                            {actionLoading === selectedOrg?.id
                              ? <Icon name="Loader2" size={14} color="currentColor" className="animate-spin" />
                              : <Icon name={selectedOrg?.is_active ? 'PauseCircle' : 'PlayCircle'} size={14} color="currentColor" />
                            }
                            {selectedOrg?.is_active ? 'Deactivate Organization' : 'Activate Organization'}
                          </button>
                          {sub && (
                            <>
                              {sub?.status !== 'active' && (
                                <button
                                  onClick={() => updateSubscriptionStatus(selectedOrg?.id, 'active')}
                                  disabled={actionLoading === selectedOrg?.id + '-sub'}
                                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium bg-success/10 text-success hover:bg-success/20 transition-smooth"
                                >
                                  <Icon name="CheckCircle" size={14} color="currentColor" />
                                  Activate Subscription
                                </button>
                              )}
                              {sub?.status !== 'cancelled' && (
                                <button
                                  onClick={() => updateSubscriptionStatus(selectedOrg?.id, 'cancelled')}
                                  disabled={actionLoading === selectedOrg?.id + '-sub'}
                                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-smooth"
                                >
                                  <Icon name="XCircle" size={14} color="currentColor" />
                                  Cancel Subscription
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => navigate('/organization-settings')}
                            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium border border-border hover:bg-muted transition-smooth"
                          >
                            <Icon name="Settings" size={14} color="currentColor" />
                            View Settings
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default AdminOperatorDashboard;
